import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from 'convex/react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Icons } from '@/components/ui/icons';
import { useState, useEffect, useRef } from 'react';
import { toast } from '@/template/lib/toast';
import { useTranslation } from 'react-i18next';
import { type CountdownAudience, type CountdownFormState, emptyCountdownForm, toCountdownForm, shouldSyncCountdownForm } from '@/lib/operator-countdown-form';
import { PlatformInvoiceSettingsCard } from '@/components/features/operator/platform-invoice-settings-card';

interface CountdownSettingsCardProps {
  idPrefix: string;
  title: string;
  description: string;
  countdown: CountdownFormState;
  setCountdown: (next: CountdownFormState) => void;
  onSave: () => void;
  saving: boolean;
  t: (key: string, options?: any) => string;
}

const toPercentage = (rate: number): number => Math.round(rate * 1000) / 10;

type CommissionRateField = 'regularShortDurationRate' | 'regularLongDurationRate' | 'mvzShortDurationRate' | 'mvzLongDurationRate' | 'vatRate';
type SubstituteRateField = 'feeRate' | 'vatRate';

const CountdownSettingsCard = ({ idPrefix, title, description, countdown, setCountdown, onSave, saving, t }: CountdownSettingsCardProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className='space-y-6'>
        <div className='space-y-2'>
          <Label htmlFor={`${idPrefix}-countdownDate`}>{t('settingsPage.countdown.targetDate')}</Label>
          <Input id={`${idPrefix}-countdownDate`} type='datetime-local' value={countdown.date} onChange={(e) => setCountdown({ ...countdown, date: e.target.value })} className='w-full max-w-md' />
          <p className='text-xs text-muted-foreground'>{t('settingsPage.countdown.targetDateHelp')}</p>
        </div>

        <div className='space-y-2'>
          <Label htmlFor={`${idPrefix}-countdownHeadline`}>{t('settingsPage.countdown.headline')}</Label>
          <Input id={`${idPrefix}-countdownHeadline`} type='text' placeholder={t('settingsPage.countdown.headlinePlaceholder')} value={countdown.headline} onChange={(e) => setCountdown({ ...countdown, headline: e.target.value })} className='w-full' />
          <p className='text-xs text-muted-foreground'>{t('settingsPage.countdown.headlineHelp')}</p>
        </div>

        <div className='space-y-2'>
          <Label htmlFor={`${idPrefix}-countdownSubheadline`}>{t('settingsPage.countdown.subheadline')}</Label>
          <Input
            id={`${idPrefix}-countdownSubheadline`}
            type='text'
            placeholder={t('settingsPage.countdown.subheadlinePlaceholder')}
            value={countdown.subheadline}
            onChange={(e) => setCountdown({ ...countdown, subheadline: e.target.value })}
            className='w-full'
          />
          <p className='text-xs text-muted-foreground'>{t('settingsPage.countdown.subheadlineHelp')}</p>
        </div>

        <Button onClick={onSave} disabled={saving} className='w-full'>
          {saving && <Icons.Loader className='mr-2 h-4 w-4 animate-spin' />}
          {saving ? t('settingsPage.actions.saving') : t('settingsPage.actions.saveCountdown')}
        </Button>
      </CardContent>
    </Card>
  );
};

const OperatorsSettingsPage = () => {
  const { t } = useTranslation('operator');
  // Queries
  const commissionRates = useQuery(api.app.settings.queries.getCommissionRates as any, {});
  const substituteFeeRate = useQuery(api.app.settings.queries.getSubstituteFeeRate as any, {});
  const providerCountdownSettings = useQuery(api.app.generalSettings.getCountdown, { audience: 'provider' });
  const substituteCountdownSettings = useQuery(api.app.generalSettings.getCountdown, { audience: 'substitute' });

  // Mutations
  const saveCommissionRates = useMutation(api.app.settings.mutations.upsertCommissionRates as any);
  const saveSubstituteFeeRate = useMutation(api.app.settings.mutations.upsertSubstituteFeeRate as any);
  const updateCountdown = useMutation(api.app.generalSettings.updateCountdown);

  // Commission rates state (DA-329: separate rates for regular and MVZ providers)
  const [regularShortDurationRate, setRegularShortDurationRate] = useState(10.0);
  const [regularLongDurationRate, setRegularLongDurationRate] = useState(7.5);
  const [mvzShortDurationRate, setMvzShortDurationRate] = useState(7.0);
  const [mvzLongDurationRate, setMvzLongDurationRate] = useState(4.5);
  const [durationThreshold, setDurationThreshold] = useState(9.5);
  const [providerVatRate, setProviderVatRate] = useState(19);

  // Substitute fee rate state
  const [substituteFee, setSubstituteFee] = useState(3.0);
  const [substituteVatRate, setSubstituteVatRate] = useState(19);
  const commissionRateEdits = useRef<Partial<Record<CommissionRateField, number>>>({});
  const substituteRateEdits = useRef<Partial<Record<SubstituteRateField, number>>>({});

  const updateCommissionRate = (field: CommissionRateField, setRate: (value: number) => void, input: string): void => {
    const value = parseFloat(input) || 0;
    commissionRateEdits.current[field] = value / 100;
    setRate(value);
  };

  const updateSubstituteRate = (field: SubstituteRateField, setRate: (value: number) => void, input: string): void => {
    const value = parseFloat(input) || 0;
    substituteRateEdits.current[field] = value / 100;
    setRate(value);
  };

  // Countdown state
  const [providerCountdown, setProviderCountdown] = useState<CountdownFormState>(() => emptyCountdownForm());
  const [substituteCountdown, setSubstituteCountdown] = useState<CountdownFormState>(() => emptyCountdownForm());

  // Track whether the operator has started editing each countdown form. Once a
  // form is dirty we never overwrite it from the server, so a reactive query
  // emission (initial resolve, re-subscription, or the operator's own save)
  // can't clobber in-progress edits.
  const providerCountdownDirty = useRef(false);
  const substituteCountdownDirty = useRef(false);

  const handleProviderCountdownChange = (next: CountdownFormState): void => {
    providerCountdownDirty.current = true;
    setProviderCountdown(next);
  };
  const handleSubstituteCountdownChange = (next: CountdownFormState): void => {
    substituteCountdownDirty.current = true;
    setSubstituteCountdown(next);
  };

  // Loading states
  const [savingCommission, setSavingCommission] = useState(false);
  const [savingSubstitute, setSavingSubstitute] = useState(false);
  const [savingProviderCountdown, setSavingProviderCountdown] = useState(false);
  const [savingSubstituteCountdown, setSavingSubstituteCountdown] = useState(false);

  // Load commission rates (DA-329: separate rates for regular and MVZ providers)
  useEffect(() => {
    if (commissionRates) {
      commissionRateEdits.current = {};
      setRegularShortDurationRate(toPercentage(commissionRates.regularShortDurationRate));
      setRegularLongDurationRate(toPercentage(commissionRates.regularLongDurationRate));
      setMvzShortDurationRate(toPercentage(commissionRates.mvzShortDurationRate));
      setMvzLongDurationRate(toPercentage(commissionRates.mvzLongDurationRate));
      setDurationThreshold(commissionRates.durationThreshold);
      setProviderVatRate(toPercentage(commissionRates.vatRate));
    }
  }, [commissionRates]);

  // Load substitute fee rate
  useEffect(() => {
    if (substituteFeeRate) {
      substituteRateEdits.current = {};
      setSubstituteFee(toPercentage(substituteFeeRate.feeRate));
      setSubstituteVatRate(toPercentage(substituteFeeRate.vatRate));
    }
  }, [substituteFeeRate]);

  // Sync each countdown form from the server while the operator hasn't touched
  // it (undefined === still loading). Once dirty, the form is left alone so
  // in-progress edits survive any later query emission.
  useEffect(() => {
    if (!shouldSyncCountdownForm(providerCountdownSettings, providerCountdownDirty.current)) return;
    setProviderCountdown(toCountdownForm(providerCountdownSettings));
  }, [providerCountdownSettings]);

  useEffect(() => {
    if (!shouldSyncCountdownForm(substituteCountdownSettings, substituteCountdownDirty.current)) return;
    setSubstituteCountdown(toCountdownForm(substituteCountdownSettings));
  }, [substituteCountdownSettings]);

  const handleSaveCommissionRates = async (): Promise<void> => {
    try {
      setSavingCommission(true);
      await saveCommissionRates({
        regularShortDurationRate: commissionRateEdits.current.regularShortDurationRate ?? commissionRates?.regularShortDurationRate ?? regularShortDurationRate / 100,
        regularLongDurationRate: commissionRateEdits.current.regularLongDurationRate ?? commissionRates?.regularLongDurationRate ?? regularLongDurationRate / 100,
        mvzShortDurationRate: commissionRateEdits.current.mvzShortDurationRate ?? commissionRates?.mvzShortDurationRate ?? mvzShortDurationRate / 100,
        mvzLongDurationRate: commissionRateEdits.current.mvzLongDurationRate ?? commissionRates?.mvzLongDurationRate ?? mvzLongDurationRate / 100,
        durationThreshold,
        vatRate: commissionRateEdits.current.vatRate ?? commissionRates?.vatRate ?? providerVatRate / 100,
      });
      toast.success(t('settingsPage.toasts.providerCommissionSaved'));
    } catch (error) {
      console.error('Error saving commission rates:', error);
      toast.error(t('settingsPage.toasts.providerCommissionError'));
    } finally {
      setSavingCommission(false);
    }
  };

  const handleSaveSubstituteFeeRate = async (): Promise<void> => {
    try {
      setSavingSubstitute(true);
      await saveSubstituteFeeRate({
        feeRate: substituteRateEdits.current.feeRate ?? substituteFeeRate?.feeRate ?? substituteFee / 100,
        vatRate: substituteRateEdits.current.vatRate ?? substituteFeeRate?.vatRate ?? substituteVatRate / 100,
      });
      toast.success(t('settingsPage.toasts.substituteFeeSaved'));
    } catch (error) {
      console.error('Error saving substitute fee rate:', error);
      toast.error(t('settingsPage.toasts.substituteFeeError'));
    } finally {
      setSavingSubstitute(false);
    }
  };

  const handleSaveCountdown = async (audience: CountdownAudience): Promise<void> => {
    const countdown = audience === 'provider' ? providerCountdown : substituteCountdown;
    const setSaving = audience === 'provider' ? setSavingProviderCountdown : setSavingSubstituteCountdown;
    const audienceLabel = audience === 'provider' ? t('settingsPage.countdown.providerLabel') : t('settingsPage.countdown.substituteLabel');

    try {
      setSaving(true);
      if (!countdown.date) {
        await updateCountdown({ audience });
      } else {
        const date = new Date(countdown.date);
        await updateCountdown({
          audience,
          targetDate: date.getTime(),
          ...(countdown.headline && { headline: countdown.headline }),
          ...(countdown.subheadline && { subheadline: countdown.subheadline }),
        });
      }
      // The saved value is now the server state, so the form is pristine again —
      // allow the reactive query to re-sync it (e.g. after server normalization).
      if (audience === 'provider') providerCountdownDirty.current = false;
      else substituteCountdownDirty.current = false;
      toast.success(t('settingsPage.toasts.countdownSaved', { audience: audienceLabel }));
    } catch (error) {
      console.error('Error saving countdown:', error);
      toast.error(t('settingsPage.toasts.countdownError', { audience: audienceLabel.toLowerCase() }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='p-6 space-y-6 max-w-4xl'>
      <div>
        <h1 className='text-3xl font-bold'>{t('settingsPage.title')}</h1>
        <p className='text-muted-foreground mt-1'>{t('settingsPage.subtitle')}</p>
      </div>

      <Alert>
        <Icons.Info className='h-4 w-4' />
        <AlertDescription>{t('settingsPage.alert')}</AlertDescription>
      </Alert>

      <PlatformInvoiceSettingsCard />

      {/* Provider Commission Rates */}
      <Card>
        <CardHeader>
          <CardTitle>{t('settingsPage.providerCommission.title')}</CardTitle>
          <CardDescription>{t('settingsPage.providerCommission.description')}</CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          <div className='space-y-4'>
            <div>
              <h3 className='text-sm font-medium mb-3'>{t('settingsPage.providerCommission.regularProviders')}</h3>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label htmlFor='regularShortDurationRate'>{t('settingsPage.providerCommission.shortServices')}</Label>
                  <div className='flex items-center gap-2'>
                    <Input
                      id='regularShortDurationRate'
                      type='number'
                      step='0.1'
                      min='0'
                      max='100'
                      value={regularShortDurationRate}
                      onChange={(e) => updateCommissionRate('regularShortDurationRate', setRegularShortDurationRate, e.target.value)}
                      className='w-24'
                    />
                    <span className='text-sm text-muted-foreground'>%</span>
                  </div>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='regularLongDurationRate'>{t('settingsPage.providerCommission.longServices')}</Label>
                  <div className='flex items-center gap-2'>
                    <Input id='regularLongDurationRate' type='number' step='0.1' min='0' max='100' value={regularLongDurationRate} onChange={(e) => updateCommissionRate('regularLongDurationRate', setRegularLongDurationRate, e.target.value)} className='w-24' />
                    <span className='text-sm text-muted-foreground'>%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className='pt-3 border-t'>
              <h3 className='text-sm font-medium mb-3'>{t('settingsPage.providerCommission.mvzProviders')}</h3>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label htmlFor='mvzShortDurationRate'>{t('settingsPage.providerCommission.shortServices')}</Label>
                  <div className='flex items-center gap-2'>
                    <Input id='mvzShortDurationRate' type='number' step='0.1' min='0' max='100' value={mvzShortDurationRate} onChange={(e) => updateCommissionRate('mvzShortDurationRate', setMvzShortDurationRate, e.target.value)} className='w-24' />
                    <span className='text-sm text-muted-foreground'>%</span>
                  </div>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='mvzLongDurationRate'>{t('settingsPage.providerCommission.longServices')}</Label>
                  <div className='flex items-center gap-2'>
                    <Input id='mvzLongDurationRate' type='number' step='0.1' min='0' max='100' value={mvzLongDurationRate} onChange={(e) => updateCommissionRate('mvzLongDurationRate', setMvzLongDurationRate, e.target.value)} className='w-24' />
                    <span className='text-sm text-muted-foreground'>%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className='pt-3 border-t'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label htmlFor='durationThreshold'>{t('settingsPage.providerCommission.durationThreshold')}</Label>
                  <div className='flex items-center gap-2'>
                    <Input id='durationThreshold' type='number' step='0.5' min='0' max='24' value={durationThreshold} onChange={(e) => setDurationThreshold(parseFloat(e.target.value) || 0)} className='w-24' />
                    <span className='text-sm text-muted-foreground'>{t('settingsPage.providerCommission.hours')}</span>
                  </div>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='providerVatRate'>{t('settingsPage.providerCommission.vat')}</Label>
                  <div className='flex items-center gap-2'>
                    <Input id='providerVatRate' type='number' step='1' min='0' max='100' value={providerVatRate} onChange={(e) => updateCommissionRate('vatRate', setProviderVatRate, e.target.value)} className='w-24' />
                    <span className='text-sm text-muted-foreground'>%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Button onClick={handleSaveCommissionRates} disabled={savingCommission} className='w-full'>
            {savingCommission && <Icons.Loader className='mr-2 h-4 w-4 animate-spin' />}
            {savingCommission ? t('settingsPage.actions.saving') : t('settingsPage.actions.saveProviderCommission')}
          </Button>
        </CardContent>
      </Card>

      {/* Substitute Platform Fee */}
      <Card>
        <CardHeader>
          <CardTitle>{t('settingsPage.substituteFee.title')}</CardTitle>
          <CardDescription>{t('settingsPage.substituteFee.description')}</CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <Label htmlFor='substituteFee'>{t('settingsPage.substituteFee.fee')}</Label>
              <div className='flex items-center gap-2'>
                <Input id='substituteFee' type='number' step='0.1' min='3' max='5' value={substituteFee} onChange={(e) => updateSubstituteRate('feeRate', setSubstituteFee, e.target.value)} className='w-24' />
                <span className='text-sm text-muted-foreground'>%</span>
              </div>
              <p className='text-xs text-muted-foreground'>{t('settingsPage.substituteFee.feeHint')}</p>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='substituteVatRate'>{t('settingsPage.substituteFee.vat')}</Label>
              <div className='flex items-center gap-2'>
                <Input id='substituteVatRate' type='number' step='1' min='0' max='100' value={substituteVatRate} onChange={(e) => updateSubstituteRate('vatRate', setSubstituteVatRate, e.target.value)} className='w-24' />
                <span className='text-sm text-muted-foreground'>%</span>
              </div>
              <p className='text-xs text-muted-foreground'>{t('settingsPage.substituteFee.vatHint')}</p>
            </div>
          </div>

          <Button onClick={handleSaveSubstituteFeeRate} disabled={savingSubstitute} className='w-full'>
            {savingSubstitute && <Icons.Loader className='mr-2 h-4 w-4 animate-spin' />}
            {savingSubstitute ? t('settingsPage.actions.saving') : t('settingsPage.actions.saveSubstituteFee')}
          </Button>
        </CardContent>
      </Card>

      {/* Countdown Settings */}
      <div className='space-y-4'>
        <div>
          <h2 className='text-xl font-semibold'>{t('settingsPage.countdown.sectionTitle')}</h2>
          <p className='text-sm text-muted-foreground'>{t('settingsPage.countdown.sectionDescription')}</p>
        </div>
        <CountdownSettingsCard
          idPrefix='provider'
          title={t('settingsPage.countdown.providerTitle')}
          description={t('settingsPage.countdown.providerDescription')}
          countdown={providerCountdown}
          setCountdown={handleProviderCountdownChange}
          onSave={() => void handleSaveCountdown('provider')}
          saving={savingProviderCountdown}
          t={t}
        />
        <CountdownSettingsCard
          idPrefix='substitute'
          title={t('settingsPage.countdown.substituteTitle')}
          description={t('settingsPage.countdown.substituteDescription')}
          countdown={substituteCountdown}
          setCountdown={handleSubstituteCountdownChange}
          onSave={() => void handleSaveCountdown('substitute')}
          saving={savingSubstituteCountdown}
          t={t}
        />
      </div>
    </div>
  );
};

export const Route = createFileRoute('/_protected/operators/settings')({
  component: OperatorsSettingsPage,
});
